import React from 'react';
import PropTypes from 'prop-types';

import { App } from '@meronex/app';
import {
  AlertDialog,
  NoData,
  toast,
  InfiniteScroll,
} from '@meronex/components';

import IAdd from '@meronex/icons/fi/FiPlus';
import IStores from '@meronex/icons/mdc/MdcWarehouse';
import IDelete from '@meronex/icons/fi/FiTrash2';

import { useTheme } from '@material-ui/core/styles';
import { useWindowSize } from '@react-hook/window-size';

import { storesMng } from '../../api/storesMng';
import ItemType from './ItemType';
import AddItemTypeDialog from './AddItemTypeDialog';
import EditItemTypeDialog from './EditItemTypeDialog';

import ImportItemTypesCSVDialog from './ImportItemTypesDialog';
import { can, p } from '../../../common/roles';
import ItemTypeActionsDialog from './ItemTypesActionDialog';

const ItemTypesList = React.forwardRef((props, ref) => {
  const {
    store,
    onItemTypeDelete,
    onItemTypeCreate,
    onItemTypeUpdate,
    onAddToStore,
    filterText,
    enableAddToStore,
  } = props;

  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';

  const [selectedItemType, setSelectedItemType] = React.useState();
  const [openAddItemType, setOpenAddItemType] = React.useState(false);
  const [openEditItemType, setOpenEditItemType] = React.useState(false);
  const [openDeleteAlert, setOpenDeleteAlert] = React.useState();
  const [openAddAlert, setOpenAddAlert] = React.useState(false);
  const [deleting, setDeleting] = React.useState();
  const [deletingError, setDeletingError] = React.useState();
  const [openActions, setOpenActions] = React.useState();

  const [width, height] = useWindowSize();
  const infiniteScrollRef = React.useRef(null);
  const filterTimeoutRef = React.useRef(null);

  const permissions = {
    in: can(p.stores.itemIn),
    out: can(p.stores.itemOut),
    transfer: can(p.stores.transferItem),
    auditItem: can(p.stores.auditStore),
    restore: can(p.stores.itemRestore),
    delete: can(p.stores.itemDelete),
    modify: can(p.stores.modifyItemAmount),
    archive: can(p.stores.itemArchive),
    auditStore: can(p.stores.auditItem),
    CRUDItemTypes: can(p.stores.CRUDItemTypes),
    viewPrices: can(p.stores.viewStoresPrices),
  };

  const [openImportItemTypesDialog, setOpenImportItemTypesDialog] = React.useState();

  React.useEffect(() => {
    setDeleting(false);
    setDeletingError(undefined);
  }, [selectedItemType]);

  React.useEffect(() => {
    clearTimeout(filterTimeoutRef.current);
    filterTimeoutRef.current = setTimeout(() => {
      refreshList(false);
    }, 350);
    return () => clearTimeout(filterTimeoutRef.current);
  }, [filterText]);

  const refreshList = () => {
    if (infiniteScrollRef.current) {
      infiniteScrollRef.current.refreshList(false);
    }
  };
  const refreshItems = () => {
    if (infiniteScrollRef.current) {
      infiniteScrollRef.current.refreshItems();
    }
  };

  const fetchItemTypes = async ({ limit, skip }) => {
    const itemTypes = await storesMng.getAllItemTypesCursor({
      skip,
      limit,
      filters: { filterValue: filterText },
    });
    return itemTypes;
  };

  const renderItem = (itemType, index) => {
    return (
      <ItemType
        key={itemType._id}
        permissions={permissions}
        index={index}
        store={store}
        onClick={() => {
          setSelectedItemType(itemType);
          setDeletingError(undefined);
          setOpenActions(true);
        }}
        enableAddToStore={enableAddToStore}
        data={itemType}
      />
    );
  };

  const _onItemTypeUpdate = () => {
    App.eventEmitter.emit('event-item-type-update', ItemTypesList.name);
    onItemTypeUpdate();
    refreshItems();
  };

  const deleteStoreItemType = async () => {
    if (deletingError) {
      setOpenDeleteAlert(false);
      return;
    }
    if (selectedItemType) {
      setDeleting(true);
      try {
        await storesMng.deleteStoreItemType(selectedItemType._id);
        onItemTypeDelete(() => {
          toast.success('Item type deleted', { autoClose: 800 });
          setOpenDeleteAlert(false);
          setDeleting(false);
          refreshItems();
        });
      } catch (e) {
        setDeletingError(e);
        // error handled via deletingError state
      } finally {
        setDeleting(false);
      }
    }
  };

  const addStoreItem = async (itemType) => {
    if (itemType) {
      try {
        await storesMng.addStoreItem(store._id, itemType._id);
        onAddToStore();
        refreshItems();
        setOpenAddAlert(false);
        setOpenAddItemType(false);
        toast.success('Item added to store', { autoClose: 1200 });
      } catch (e) {
        toast.error(e.reason, { autoClose: 2000 });
      }
    }
  };

  const getAlertMsg = () => {
    if (deletingError) {
      return (
        <div>
          This type is in use. {selectedItemType.name} is found in stores:
          <ul>
            {deletingError.details.map((storeItem, i) => (
              <li key={i}><IStores /> {storeItem.store.name}</li>
            ))}
          </ul>
        </div>
      );
    }
    return <div>Are you sure you want to delete <strong>{selectedItemType.name}</strong>?</div>;
  };

  const accentColor = isDark ? 'rgba(99,102,241,0.8)' : '#6366f1';

  return (
    <>
      {/* Create item type button */}
      {permissions.CRUDItemTypes && (
        <div
          onClick={() => setOpenAddItemType(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '10px 20px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: 500,
            color: accentColor,
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}`,
            transition: 'background-color 0.15s ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
          <IAdd size={15} />
          <span>Create New Item Type</span>
        </div>
      )}
      <InfiniteScroll
        ref={infiniteScrollRef}
        height={height - 220}
        remoteRowCount={9999999}
        listStyle={{ paddingLeft: '0px', paddingRight: '0px' }}
        renderItem={(data, isScrolling, index) => renderItem(data, index)}
        itemHeight={68}
        lastItemHeight={120}
        noRowRenderer={() => (
          <div>
            <NoData
              style={{ padding: '50px' }}
              onClick={() => setOpenAddItemType(true)}
              message={'No item types found'}
            />
          </div>
        )}
        getListItems={fetchItemTypes}
      />
      {openActions && (
        <ItemTypeActionsDialog
          enableAddToStore={enableAddToStore}
          open={openActions}
          itemType={selectedItemType}
          store={store}
          onAdd={(itemType) => {
            setSelectedItemType(itemType);
            addStoreItem(itemType);
            setOpenActions(false);
          }}
          onEdit={(itemType) => {
            setSelectedItemType(itemType);
            setOpenEditItemType(true);
            setOpenActions(false);
          }}
          onDelete={(itemType) => {
            setSelectedItemType(itemType);
            setOpenDeleteAlert(true);
            setOpenActions(false);
          }}
          onClose={() => setOpenActions(false)}
        />
      )}
      {openAddItemType && (
        <AddItemTypeDialog
          onAdd={(cb) => {
            if (onItemTypeCreate) onItemTypeCreate(cb);
            refreshList();
          }}
          open={openAddItemType}
          onClose={() => setOpenAddItemType(false)}
        />
      )}
      {openImportItemTypesDialog && (
        <ImportItemTypesCSVDialog
          open={openImportItemTypesDialog}
          onClose={() => setOpenImportItemTypesDialog(false)}
        />
      )}
      {selectedItemType && (
        <>
          {openEditItemType && (
            <EditItemTypeDialog
              onUpdate={_onItemTypeUpdate}
              open={openEditItemType}
              itemType={selectedItemType}
              onClose={() => setOpenEditItemType(false)}
            />
          )}
          {store && (
            <AlertDialog
              open={openAddAlert}
              onClose={() => setOpenAddAlert(false)}
              onConfirm={() => addStoreItem()}
              confirmBtn={<span style={{ color: '#16a34a' }}>Add</span>}
              description={`Add "${selectedItemType.name}" to store "${store.name}"?`}
            />
          )}
          <AlertDialog
            open={openDeleteAlert}
            onConfirm={() => deleteStoreItemType()}
            loading={deleting}
            description={getAlertMsg()}
            confirmBtn={
              deletingError
                ? <div style={{ color: '#dc2626' }}>Ok</div>
                : <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#dc2626' }}>
                    <IDelete size={14} /> Delete
                  </div>
            }
            onClose={() => setOpenDeleteAlert(false)}
          />
        </>
      )}
    </>
  );
});

ItemTypesList.propTypes = {
  store: PropTypes.object,
  onItemTypeDelete: PropTypes.func,
  onItemTypeCreate: PropTypes.func,
  onItemTypeUpdate: PropTypes.func,
  onAddToStore: PropTypes.func,
  enableAddToStore: PropTypes.bool,
  filterText: PropTypes.string,
};
ItemTypesList.defaultProps = {
  onItemTypeDelete: () => {},
  onItemTypeCreate: () => {},
  onAddToStore: () => {},
  onItemTypeUpdate: () => {},
  enableAddToStore: true,
};

export default ItemTypesList;
